import React, { useState } from 'react';

/**
 * DefinitionSelector
 *
 * Inline control for picking the active spreadsheet model definition, plus
 * affordances to create a new definition (blank or cloned from an existing
 * one), rename, and delete the active definition.
 *
 * Props:
 *   definitions  : [{ id, name, is_default }]
 *   value        : currently selected definition id
 *   onChange     : (newId) => void
 *   onCreate     : (name, copyFrom) => void  — create new definition.
 *                  ``copyFrom`` is either '__blank__' (empty 1-sheet
 *                  definition) or an existing definition id to clone.
 *   onRename     : (newName) => void         — rename current definition
 *   onDelete     : () => void               — delete currently selected definition
 *   busy         : boolean                  — disable controls while a request is in flight
 */
export default function DefinitionSelector({
  definitions,
  value,
  onChange,
  onCreate,
  onRename,
  onDelete,
  busy = false,
}) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'creating' | 'renaming'
  const [inputName, setInputName] = useState('');
  const [copyFrom, setCopyFrom] = useState('__blank__');

  const currentDef = definitions.find(d => d.id === value);
  const isDefault = currentDef?.is_default === true;

  const submitNew = () => {
    const n = inputName.trim();
    if (!n) return;
    onCreate(n, copyFrom);
    setInputName('');
    setCopyFrom('__blank__');
    setMode('idle');
  };

  const submitRename = () => {
    const n = inputName.trim();
    if (!n) return;
    onRename(n);
    setInputName('');
    setMode('idle');
  };

  const beginCreating = () => {
    setCopyFrom(value || '__blank__');
    setInputName('');
    setMode('creating');
  };

  const beginRenaming = () => {
    setInputName(currentDef?.name || '');
    setMode('renaming');
  };

  const cancel = () => { setMode('idle'); setInputName(''); };

  return (
    <div className="d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
      <label className="text-muted mb-0" style={{ fontSize: 12 }}>
        <i className="bi bi-file-earmark-spreadsheet me-1" />
        Spreadsheet:
      </label>

      {mode === 'idle' && (
        <>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 200, fontSize: 13 }}
            value={value || ''}
            disabled={busy}
            onChange={e => onChange(e.target.value)}
          >
            {definitions.map(d => (
              <option key={d.id} value={d.id}>{d.is_default ? '★ ' : ''}{d.name}</option>
            ))}
          </select>
          <button
            className="btn btn-outline-primary btn-sm"
            style={{ fontSize: 12 }}
            onClick={beginCreating}
            disabled={busy}
            title="Create a new spreadsheet definition"
          >
            <i className="bi bi-plus-lg me-1" />New
          </button>
          {onRename && (
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ fontSize: 12 }}
              onClick={beginRenaming}
              disabled={busy}
              title="Rename this definition"
            >
              <i className="bi bi-pencil" />
            </button>
          )}
          {!isDefault && (
            <button
              className="btn btn-outline-danger btn-sm"
              style={{ fontSize: 12 }}
              onClick={onDelete}
              disabled={busy}
              title="Delete this definition"
            >
              <i className="bi bi-trash3" />
            </button>
          )}
        </>
      )}

      {mode === 'renaming' && (
        <>
          <input
            type="text"
            autoFocus
            className="form-control form-control-sm"
            style={{ width: 200, fontSize: 13 }}
            placeholder="New name…"
            value={inputName}
            onChange={e => setInputName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') cancel();
            }}
          />
          <button
            className="btn btn-primary btn-sm"
            style={{ fontSize: 12 }}
            onClick={submitRename}
            disabled={!inputName.trim() || busy}
          >
            Rename
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{ fontSize: 12 }}
            onClick={cancel}
            disabled={busy}
          >
            Cancel
          </button>
        </>
      )}

      {mode === 'creating' && (
        <>
          <input
            type="text"
            autoFocus
            className="form-control form-control-sm"
            style={{ width: 200, fontSize: 13 }}
            placeholder="New spreadsheet name…"
            value={inputName}
            onChange={e => setInputName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submitNew();
              if (e.key === 'Escape') cancel();
            }}
          />
          <label className="text-muted mb-0" style={{ fontSize: 12 }}>Start from:</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 180, fontSize: 13 }}
            value={copyFrom}
            disabled={busy}
            onChange={e => setCopyFrom(e.target.value)}
            title="Start from a blank spreadsheet or clone an existing definition"
          >
            <option value="__blank__">Blank (1 empty sheet)</option>
            {definitions.map(d => (
              <option key={d.id} value={d.id}>Clone: {d.name}</option>
            ))}
          </select>
          <button
            className="btn btn-primary btn-sm"
            style={{ fontSize: 12 }}
            onClick={submitNew}
            disabled={!inputName.trim() || busy}
          >
            Create
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{ fontSize: 12 }}
            onClick={cancel}
            disabled={busy}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}
